import {useEffect, useState} from "react";
import {useAnalytics} from "@unibuddy/tracking";
import {useErrorReporting} from "@unibuddy/error-reporting";
import {Button, Heading, LoadingButton, Stack, Text, View} from "@unibuddy/patron";
import {useNavigate} from "../Hooks/useNavigate";
import {Routes, TrackEvents} from "../constants";
import {SpaceType, useSpaces} from "../StudentApp/SpacesProvider";
import {useUpdateUniversityLinkMutation} from "../types";

export enum MarketingConsentStrings {
    Heading = "📬 University Updates to Guide Your Decision",
    Subheading = "Would you like to receive marketing updates from the universities you've engaged with, to help you make the best possible choice? You can unsubscribe at any time.",
    AcceptButton = "Yes, please!",
    IgnoreButton = "Maybe later",
}

export const MarketingConsent = () => {
    const {navigate} = useNavigate();
    const {spaces} = useSpaces();
    const [updateLink] = useUpdateUniversityLinkMutation();
    const [loading, setLoading] = useState(false);
    const {reportError} = useErrorReporting();
    const {trackEvent} = useAnalytics();

    useEffect(() => {
        trackEvent(TrackEvents.MARKETING_CONSENT_SCREEN_SHOWN);
    }, [trackEvent]);

    const handleDecline = () => {
        trackEvent(TrackEvents.MARKETING_CONSENT_DECLINED);
        navigate({
            path: Routes.ROOT,
        });
    };

    const handleAccept = async () => {
        setLoading(true);
        const links = spaces.filter((space) => space.type === SpaceType.UNIVERSITY);
        const slugs = links.map((space) => space.slug);

        const promises = slugs.map(async (slug) => {
            try {
                await updateLink({
                    variables: {
                        universitySlug: slug,
                        acceptMarketing: true,
                    },
                });
            } catch (error) {
                console.error(`Error updating university link for slug ${slug}:`, error);
                reportError({
                    message: `Error updating university link for slug ${slug}`,
                    name: "MarketingConsentError",
                    cause: error,
                    stack: error.stack,
                });
            }
        });

        try {
            await Promise.all(promises);
            navigate({
                path: Routes.ROOT,
            });
            trackEvent(TrackEvents.MARKETING_CONSENT_ACCEPTED);
        } catch (error) {
            console.error("Unexpected error during update:", error);
            reportError({
                message: "Unexpected error during update",
                name: "MarketingConsentError",
                cause: error,
                stack: error.stack,
            });
        } finally {
            setLoading(false);
        }
    };

    return (
        <View flex="1" padding="medium" justifyContent="center">
            <Stack space="large">
                <Heading align="center" level="2" size="xsmall">
                    {MarketingConsentStrings.Heading}
                </Heading>
                <Text align="center">{MarketingConsentStrings.Subheading}</Text>
            </Stack>
            <View paddingTop="xlarge">
                <Stack space="medium">
                    <LoadingButton loading={loading} color="primary" block onClick={handleAccept}>
                        {MarketingConsentStrings.AcceptButton}
                    </LoadingButton>
                    <Button disabled={loading} clear block onClick={handleDecline}>
                        {MarketingConsentStrings.IgnoreButton}
                    </Button>
                </Stack>
            </View>
        </View>
    );
};
